import React, { useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Popover,
  CircularProgress,
} from '@mui/material';
import { useForm, Controller, useFieldArray } from 'react-hook-form';
import { TwitterPicker } from 'react-color';
import { colorPickerConstants } from '../../constants/colors';

const NerisGroupsForm = ({ initialData, onSubmit, loading }) => {
  const [colorPickerAnchor, setColorPickerAnchor] = useState({
    element: null,
    index: null,
  });

  const {
    control,
    handleSubmit,
    setValue,
    watch,
    formState: { isDirty },
  } = useForm({
    values: {
      groups: initialData,
    },
  });

  const { fields } = useFieldArray({
    control,
    name: 'groups',
  });

  const watchedGroups = watch('groups');

  const handleFormSubmit = (data) => {
    onSubmit(data.groups);
  };

  const handleColorClick = (event, index) => {
    setColorPickerAnchor({
      element: event.currentTarget,
      index,
    });
  };

  const handleColorClose = () => {
    setColorPickerAnchor({
      element: null,
      index: null,
    });
  };

  const handleColorChange = (color, index) => {
    setValue(`groups.${index}.color`, color.hex, { shouldDirty: true });
    handleColorClose();
  };

  return (
    <>
      <form onSubmit={handleSubmit(handleFormSubmit)}>
        <Box sx={{ mb: 3 }}>
          {/* Header */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: '220px 400px 150px',
              gap: 2,
              mb: 2,
              alignItems: 'center',
            }}
          >
            <Typography variant="body2" color="text.secondary">
              Neris name
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Custom name
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Color
            </Typography>
          </Box>

          {/* Form Fields */}
          {fields.map((field, index) => (
            <Box
              key={field.id}
              sx={{
                display: 'grid',
                gridTemplateColumns: '220px 400px 150px',
                gap: 2,
                mb: 2,
                alignItems: 'center',
                py: 1,
              }}
            >
              {/* Neris Name (readonly) */}
              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                {field.neris_name}
              </Typography>

              {/* Custom Name Input */}
              <Controller
                name={`groups.${index}.name`}
                control={control}
                render={({ field: inputField }) => (
                  <TextField
                    {...inputField}
                    placeholder="Write"
                    variant="outlined"
                    size="small"
                    fullWidth
                  />
                )}
              />

              {/* Color Picker */}
              <Box
                onClick={(e) => handleColorClick(e, index)}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  cursor: 'pointer',
                  p: 1,
                  borderRadius: 1,
                  border: '1px solid #C4C4C4',
                  backgroundColor: '#fff',
                  minHeight: 40,
                  '&:hover': {
                    borderColor: '#000000de',
                  },
                  maxWidth: '150px',
                }}
              >
                <Box
                  sx={{
                    width: 16,
                    height: 16,
                    borderRadius: '50%',
                    backgroundColor: watchedGroups[index]?.color || field.color,
                    flexShrink: 0,
                  }}
                />
                <Typography variant="body2">
                  {colorPickerConstants.getColorName(
                    watchedGroups[index]?.color || field.color
                  )}
                </Typography>
              </Box>
            </Box>
          ))}
        </Box>

        {/* Color Picker Popover */}
        <Popover
          open={Boolean(colorPickerAnchor.element)}
          anchorEl={colorPickerAnchor.element}
          onClose={handleColorClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
        >
          <TwitterPicker
            color={
              colorPickerAnchor.index !== null
                ? watchedGroups[colorPickerAnchor.index]?.color || '#E53935'
                : '#E53935'
            }
            colors={colorPickerConstants.colorPalette}
            onChange={(color) =>
              colorPickerAnchor.index !== null &&
              handleColorChange(color, colorPickerAnchor.index)
            }
            triangle="hide"
          />
        </Popover>

        <Button
          type="submit"
          variant="contained"
          disabled={loading || !isDirty}
          sx={{ minWidth: 100 }}
        >
          {loading ? <CircularProgress size={24} color="inherit" /> : 'Save'}
        </Button>
      </form>
    </>
  );
};

export default NerisGroupsForm;
